import { ExternalMemberID, ProviderNetworkMemberID } from "@repo/db";
import { asMDICaseID, asMDIPatientID } from "@repo/db/branded-types";
import {
  Case,
  CASE_STATUS,
  mapSDKStatusToCaseStatus,
} from "@repo/provider-network";
import { BRAND } from "@repo/tailwind-config/brand";
import type { AvatarProps } from "@repo/ui/avatar";
import { formatClinicianShortName } from "src/lib/clinician-name";
import { KinsaInitialContext } from "src/lib/definitions";
import { getVisitUrl } from "src/lib/routes";
import {
  getAvatarFallback,
  getPatientAvatar,
} from "src/lib/utils/avatar-utils";

type CoveredPersons = KinsaInitialContext["account"]["coveredPersons"];
type PatientMapping = Array<{
  providerNetworkMemberId?: ProviderNetworkMemberID | null;
  externalMemberId?: ExternalMemberID | null;
}>;

export interface PastVisitDisplay {
  title: string;
  dateText: string;
  firstAvatar: AvatarProps["variant"];
  firstAvatarInitial?: string;
  secondAvatar: "custom" | "unassignedDoc";
  secondAvatarUrl?: string;
  href?: string;
  cancelled: boolean;
}

function formatShortDate(timestamp: string): string {
  return new Date(timestamp).toLocaleDateString("en-US", {
    year: "2-digit",
    month: "numeric",
    day: "numeric",
  });
}

function getPastVisitTitle(visit: Case): string {
  const patientFirstName = visit.patient?.first_name || "";
  const doctorName = formatClinicianShortName(visit.case_assignment?.clinician);

  if (patientFirstName && doctorName) {
    return `${patientFirstName} & ${doctorName}`;
  }

  const fullName = visit.patient
    ? `${visit.patient.first_name || ""} ${visit.patient.last_name || ""}`.trim()
    : "";
  return fullName || patientFirstName || "Visit";
}

function getPastVisitHref(visit: Case): string | undefined {
  const status = mapSDKStatusToCaseStatus(visit.case_status?.name);
  // Only completed past visits link to chat history. Cancelled visits are not
  // navigable.
  if (status !== CASE_STATUS.COMPLETED) return undefined;
  const caseId = visit.case_id || visit.id || "";
  const patientId = visit.patient?.patient_id || "";
  return getVisitUrl(asMDIPatientID(patientId), asMDICaseID(caseId));
}

function resolvePatientAvatar(
  visit: Case,
  coveredPersons?: CoveredPersons,
  patientMapping?: PatientMapping
): AvatarProps["variant"] {
  if (!coveredPersons || !visit.patient) {
    return getAvatarFallback(
      visit.patient?.patient_id ||
        `${visit.patient?.first_name || ""}${visit.patient?.last_name || ""}`
    );
  }

  // Prefer ID-based matching via patientMapping.
  const providerPatientId = visit.patient?.patient_id;
  let matched: CoveredPersons[number] | undefined;
  if (patientMapping && providerPatientId) {
    const entry = patientMapping.find(
      (p) => p.providerNetworkMemberId === providerPatientId
    );
    if (entry?.externalMemberId) {
      matched = coveredPersons.find((p) => p.id === entry.externalMemberId);
    }
  }

  // Fall back to name matching — tolerant of missing lastName (Owlet shape).
  if (!matched) {
    const visitFirst = visit.patient?.first_name?.toLowerCase();
    const visitLast = visit.patient?.last_name?.toLowerCase();
    matched = coveredPersons.find((person) => {
      const pFirst = person.firstName?.toLowerCase();
      const pLast = (person as { lastName?: string }).lastName?.toLowerCase();
      if (!pFirst || !visitFirst) return false;
      if (pFirst !== visitFirst) return false;
      if (!pLast || !visitLast) return true;
      return pLast === visitLast;
    });
  }

  if (matched) return getPatientAvatar(matched.id, coveredPersons);
  return getAvatarFallback(visit.patient.patient_id || "");
}

export function derivePastVisitDisplay(
  visit: Case,
  coveredPersons?: CoveredPersons,
  patientMapping?: PatientMapping
): PastVisitDisplay {
  const status = mapSDKStatusToCaseStatus(visit.case_status?.name);
  const clinician = visit.case_assignment?.clinician;
  const hasClinicianPhoto = !!clinician?.photo;
  const useInitial = BRAND === "owlet";
  const patientFirstName = visit.patient?.first_name || "";

  return {
    title: getPastVisitTitle(visit),
    dateText: formatShortDate(visit.updated_at || visit.created_at || ""),
    firstAvatar: useInitial
      ? "initial"
      : resolvePatientAvatar(visit, coveredPersons, patientMapping),
    firstAvatarInitial: useInitial ? patientFirstName : undefined,
    secondAvatar: clinician && hasClinicianPhoto ? "custom" : "unassignedDoc",
    secondAvatarUrl:
      clinician?.photo?.url_thumbnail || clinician?.photo?.url || undefined,
    href: getPastVisitHref(visit),
    cancelled: status === CASE_STATUS.CANCELLED,
  };
}
